import { API_BASE_URL, getAuthHeaders, getAuthToken, handleApiError } from './apiUtils';
import type {
    BranchLedgerResponse,
    BranchOption,
    CodReceipt,
    CodReceiptListResponse,
    DeliverySheetConsignment,
    DeliverySheetOption,
    DepositSlip,
    DepositSlipListResponse,
    RiderOption,
} from '../pages/BranchFinances/types/branchFinance.types';

const buildQueryString = (params: Record<string, string | number | undefined | null>) => {
    const query = new URLSearchParams();
    Object.entries(params).forEach(([key, value]) => {
        if (value !== undefined && value !== null && value !== '') {
            query.append(key, String(value));
        }
    });
    const qs = query.toString();
    return qs ? `?${qs}` : '';
};

export const branchFinanceApi = {
    getBranches: async (): Promise<BranchOption[]> => {
        const response = await fetch(`${API_BASE_URL}/finance/branch-ledger/branches`, {
            headers: getAuthHeaders(),
        });

        if (!response.ok) {
            await handleApiError(response);
        }

        const payload = await response.json();
        return payload.data as BranchOption[];
    },

    getLedger: async (params: { branchId: number; fromDate?: string; toDate?: string }): Promise<BranchLedgerResponse> => {
        const queryString = buildQueryString({
            branch_id: params.branchId,
            from_date: params.fromDate,
            to_date: params.toDate,
        });

        const response = await fetch(`${API_BASE_URL}/finance/branch-ledger${queryString}`, {
            headers: getAuthHeaders(),
        });

        if (!response.ok) {
            await handleApiError(response);
        }

        return (await response.json()) as BranchLedgerResponse;
    },

    downloadLedger: async (params: { branchId: number; fromDate?: string; toDate?: string }): Promise<Blob> => {
        const queryString = buildQueryString({
            branch_id: params.branchId,
            from_date: params.fromDate,
            to_date: params.toDate,
        });

        const response = await fetch(`${API_BASE_URL}/finance/branch-ledger/export${queryString}`, {
            headers: getAuthHeaders(),
        });

        if (!response.ok) {
            await handleApiError(response);
        }

        return await response.blob();
    },

    getCodReceipts: async (params: {
        branchId?: number;
        riderId?: number;
        page?: number;
        perPage?: number;
    }): Promise<CodReceiptListResponse> => {
        const queryString = buildQueryString({
            branch_id: params.branchId,
            rider_id: params.riderId,
            page: params.page,
            per_page: params.perPage,
        });

        const response = await fetch(`${API_BASE_URL}/finance/cod-receipts${queryString}`, {
            headers: getAuthHeaders(),
        });

        if (!response.ok) {
            await handleApiError(response);
        }

        const payload = await response.json();
        // Handle both wrapped and unwrapped responses
        if (payload.data && Array.isArray(payload.data)) {
            return payload as CodReceiptListResponse;
        }
        // If response is already in the correct format
        return payload as CodReceiptListResponse;
    },

    createCodReceipt: async (payload: {
        rider_id: number;
        deliverysheet_id: number;
        consignment_ids: number[];
    }): Promise<CodReceipt> => {
        const response = await fetch(`${API_BASE_URL}/finance/cod-receipts`, {
            method: 'POST',
            headers: {
                ...getAuthHeaders(),
            },
            body: (() => {
                const formData = new FormData();
                formData.append('rider_id', String(payload.rider_id));
                formData.append('deliverysheet_id', String(payload.deliverysheet_id));
                payload.consignment_ids.forEach((id, index) => {
                    formData.append(`consignment_ids[${index}]`, String(id));
                });
                return formData;
            })(),
        });

        if (!response.ok) {
            await handleApiError(response);
        }

        const result = await response.json();
        return result.data as CodReceipt;
    },

    getCodRiders: async (params: { branchId?: number } = {}): Promise<RiderOption[]> => {
        const queryString = buildQueryString({
            branch_id: params.branchId,
        });

        const response = await fetch(`${API_BASE_URL}/finance/cod-receipts/riders${queryString}`, {
            headers: getAuthHeaders(),
        });

        if (!response.ok) {
            await handleApiError(response);
        }

        const payload = await response.json();
        return payload.data as RiderOption[];
    },

    getCodDeliverySheets: async (riderId: number): Promise<DeliverySheetOption[]> => {
        const response = await fetch(
            `${API_BASE_URL}/finance/cod-receipts/riders/${riderId}/delivery-sheets`,
            {
                headers: getAuthHeaders(),
            }
        );

        if (!response.ok) {
            await handleApiError(response);
        }

        const payload = await response.json();
        return payload.data as DeliverySheetOption[];
    },

    getCodConsignments: async (deliverySheetId: number): Promise<DeliverySheetConsignment[]> => {
        const response = await fetch(
            `${API_BASE_URL}/finance/cod-receipts/delivery-sheets/${deliverySheetId}/consignments`,
            {
                headers: getAuthHeaders(),
            }
        );

        if (!response.ok) {
            await handleApiError(response);
        }

        const payload = await response.json();
        return payload.data as DeliverySheetConsignment[];
    },

    getDepositSlips: async (params: {
        branchId?: number;
        status?: number;
        page?: number;
        perPage?: number;
    }): Promise<DepositSlipListResponse> => {
        const queryString = buildQueryString({
            branch_id: params.branchId,
            status: params.status,
            page: params.page,
            per_page: params.perPage,
        });

        const response = await fetch(`${API_BASE_URL}/finance/deposit-slips${queryString}`, {
            headers: getAuthHeaders(),
        });

        if (!response.ok) {
            await handleApiError(response);
        }

        const payload = await response.json();
        // Handle both wrapped and unwrapped responses
        if (payload.data && Array.isArray(payload.data)) {
            return payload as DepositSlipListResponse;
        }
        // If response is already in the correct format
        return payload as DepositSlipListResponse;
    },

    createDepositSlip: async (payload: {
        slip_no: string;
        amount: number;
        deposit_type: string;
        attachment: File;
    }): Promise<DepositSlip> => {
        // Verify the file is valid
        if (!(payload.attachment instanceof File)) {
            throw new Error('Invalid file object');
        }

        const formData = new FormData();
        formData.append('slip_no', payload.slip_no);
        formData.append('amount', String(payload.amount));
        formData.append('deposit_type', payload.deposit_type);
        // Append file with explicit filename to ensure proper upload
        formData.append('attachment', payload.attachment, payload.attachment.name);

        // For FormData, don't set Content-Type - browser will set it automatically with boundary
        const headers = getAuthHeaders();

        const response = await fetch(`${API_BASE_URL}/finance/deposit-slips`, {
            method: 'POST',
            headers: headers,
            body: formData,
        });

        if (!response.ok) {
            await handleApiError(response);
        }

        const result = await response.json();
        return result.data as DepositSlip;
    },

    updateDepositSlip: async (
        depositSlipId: number,
        payload: {
            slip_no: string;
            amount: number;
            deposit_type: string;
            attachment?: File | null;
        }
    ): Promise<DepositSlip> => {
        const formData = new FormData();
        formData.append('slip_no', payload.slip_no);
        formData.append('amount', String(payload.amount));
        formData.append('deposit_type', payload.deposit_type);
        if (payload.attachment && payload.attachment instanceof File) {
            // Append file with explicit filename to ensure proper upload
            formData.append('attachment', payload.attachment, payload.attachment.name);
        }

        // For FormData, don't set Content-Type - browser will set it automatically with boundary
        const headers = getAuthHeaders();

        const response = await fetch(`${API_BASE_URL}/finance/deposit-slips/${depositSlipId}`, {
            method: 'PUT',
            headers: headers,
            body: formData,
        });

        if (!response.ok) {
            await handleApiError(response);
        }

        const result = await response.json();
        return result.data as DepositSlip;
    },

    approveDepositSlip: async (depositSlipId: number, payload: { pwd: string }): Promise<DepositSlip> => {
        const response = await fetch(
            `${API_BASE_URL}/finance/deposit-slips/${depositSlipId}/approve`,
            {
                method: 'PATCH',
                headers: {
                    ...getAuthHeaders(),
                    'Content-Type': 'application/json',
                },
                body: JSON.stringify({ pwd: payload.pwd }),
            }
        );

        if (!response.ok) {
            await handleApiError(response);
        }

        const result = await response.json();
        return result.data as DepositSlip;
    },

    rejectDepositSlip: async (
        depositSlipId: number,
        payload: { reason: string; reason_attachment?: File | null }
    ): Promise<DepositSlip> => {
        const formData = new FormData();
        // Always append reason - ensure it's a string
        formData.append('reason', String(payload.reason || ''));
        // Add _method for Laravel method spoofing with FormData
        formData.append('_method', 'PATCH');
        if (payload.reason_attachment && payload.reason_attachment instanceof File) {
            // Append file with explicit filename to ensure proper upload
            formData.append('reason_attachment', payload.reason_attachment, payload.reason_attachment.name);
        }

        // For FormData, don't set Content-Type - browser will set it automatically with boundary
        const token = getAuthToken();
        const headers: HeadersInit = {
            Accept: 'application/json',
            'X-Requested-With': 'XMLHttpRequest',
        };
        if (token) {
            headers['Authorization'] = `Bearer ${token}`;
        }
        // Explicitly don't set Content-Type - let browser set it with boundary for FormData

        const response = await fetch(
            `${API_BASE_URL}/finance/deposit-slips/${depositSlipId}/reject`,
            {
                method: 'POST', // Use POST with _method=PATCH for FormData
                headers: headers,
                body: formData,
            }
        );

        if (!response.ok) {
            await handleApiError(response);
        }

        const result = await response.json();
        return result.data as DepositSlip;
    },

    downloadDepositSlipAttachment: async (
        depositSlipId: number,
        type: 'primary' | 'reason' = 'primary'
    ): Promise<{ blob: Blob; contentType: string }> => {
        const queryString = buildQueryString({ type });
        const response = await fetch(
            `${API_BASE_URL}/finance/deposit-slips/${depositSlipId}/attachment${queryString}`,
            {
                headers: getAuthHeaders(),
            }
        );

        if (!response.ok) {
            await handleApiError(response);
        }

        const blob = await response.blob();
        const contentType = response.headers.get('content-type') || 'application/pdf';
        return { blob, contentType };
    },
};

